import {
  AddToCalendarButton,
  AddToCalendarLink,
  ButtonContainer,
  ButtonDiv,
  Container,
  InfoContainer,
  InfoSpecialContainer,
} from './style';
import { Button, Flex, Loader } from '@mantine/core';
import { useIntl } from 'react-intl';
import { convertTimeFormatTo12 } from '@jadwalh/utils';
import dayjs from 'dayjs';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ReactComponent as SuccessRes } from '../../assets/images/svg/SuccessRes.svg';
import { getLangState } from '../../store/lang.slice';
import { useSelector, useDispatch } from 'react-redux';
import {
  getAppointmentData,
  setAppointmentData,
} from '../../store/appointmentData.slice';
import { getSettingsState } from '../../store/settings.slice';
import { setTenantSlug } from '../../store/tenantSlug.slice';
import { useEffectOnce } from 'react-use';
import { CalendarEvent, google, ics } from 'calendar-link';
import EventInfo from '../scheduleMeeting/publicScheduleMeeting/components/EventInfo';
import { ReactComponent as GoogleIcon } from '../../assets/images/svg/google-svgrepo.svg';
import { ReactComponent as ICS } from '../../assets/images/svg/ICS.svg';
import { useCallback, useEffect, useRef, useState } from 'react';
import { getHangoutLink } from '../../api/appointment';
import { getPaymentStatus, getAppointmentDetails } from '../../api/payment';
import type { PaymentStatusResponse } from '../../api/payment';
import { isRoundRobinEvent } from '../../utils/roundRobin';
import {
  clearAppointmentSuccessSession,
  readAppointmentSuccessContext,
  readPaymentContext,
} from '../../utils/appointmentSuccessContext';
import {
  isSummaryComplete,
  mapAppointmentDetailsToSummary,
  mapStoredContextToSummary,
  mapSummaryToAppointmentData,
  mergeAppointmentSummary,
  type AppointmentSummary,
} from '../../utils/appointmentSummary';

const SuccessReservation = () => {
  const { formatMessage } = useIntl();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const langType = useSelector(getLangState);
  const reduxAppointment = useSelector(getAppointmentData);
  const settings = useSelector(getSettingsState);
  const textColor = settings?.textColor;
  const [searchParams] = useSearchParams();
  const [isLoadingHangout, setIsLoadingHangout] = useState(false);
  const [hangoutLinkState, setHangoutLinkState] = useState<string | null>(null);
  const [calendarEventData, setCalendarEventData] = useState<{
    description?: string;
    attendees?: string;
  } | null>(null);

  const [stablePaymentContext] = useState(() => readPaymentContext());
  const [stableStoredContext] = useState(() => readAppointmentSuccessContext());

  const tenantSlug = searchParams.get('tenant');
  const canceled = searchParams.get('canceled');
  const clientSlug = searchParams.get('client');
  const eventSlug = searchParams.get('event');
  const appointmentId = searchParams.get('id');

  const paymentId =
    searchParams.get('payment_id') ??
    (stablePaymentContext?.payment_id
      ? String(stablePaymentContext.payment_id)
      : null);
  const accessToken =
    searchParams.get('access_token') ??
    stablePaymentContext?.access_token ??
    (typeof stableStoredContext?.access_token === 'string'
      ? stableStoredContext.access_token
      : null);
  const resolvedAppointmentId =
    appointmentId ?? stablePaymentContext?.appointment_nr ?? null;

  const [paymentState, setPaymentState] =
    useState<PaymentStatusResponse | null>(null);
  const [isVerifyingPayment, setIsVerifyingPayment] = useState(false);
  const [effectiveAppointment, setEffectiveAppointment] =
    useState<AppointmentSummary | null>(() =>
      mergeAppointmentSummary(
        mapStoredContextToSummary(stableStoredContext),
        reduxAppointment as Partial<AppointmentSummary>
      )
    );
  const [hydrationState, setHydrationState] = useState<
    'loading' | 'ready' | 'error'
  >(() =>
    mergeAppointmentSummary(
      mapStoredContextToSummary(stableStoredContext),
      reduxAppointment as Partial<AppointmentSummary>
    )
      ? 'ready'
      : 'loading'
  );
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const hydrationStartedRef = useRef(false);

  const needsPaymentVerification = Boolean(
    paymentId && tenantSlug && accessToken
  );
  const paymentConfirmed = paymentState?.status === 'paid';
  const paymentFailed =
    paymentState?.status === 'failed' || paymentState?.status === 'expired';

  const applySummary = useCallback(
    (summary: AppointmentSummary) => {
      setEffectiveAppointment(summary);
      dispatch(setAppointmentData(mapSummaryToAppointmentData(summary)));
      setHydrationState('ready');
      clearAppointmentSuccessSession();
    },
    [dispatch]
  );

  useEffect(() => {
    if (tenantSlug) {
      dispatch(setTenantSlug(tenantSlug));
    } else {
      dispatch(setTenantSlug(''));
    }
  }, [tenantSlug, dispatch]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    if (!needsPaymentVerification) {
      return;
    }

    dispatch(setTenantSlug(tenantSlug!));

    let attempts = 0;
    const maxAttempts = 30;
    setIsVerifyingPayment(true);

    const poll = async (): Promise<void> => {
      attempts++;

      try {
        const result = await getPaymentStatus(
          paymentId!,
          accessToken,
          tenantSlug
        );
        setPaymentState(result);

        if (result.status !== 'pending' || attempts >= maxAttempts) {
          if (pollRef.current) {
            clearInterval(pollRef.current);
            pollRef.current = null;
          }

          if (result.status === 'pending' && attempts >= maxAttempts) {
            setPaymentState({ status: 'failed', paid_at: null });
          }

          setIsVerifyingPayment(false);
        }
      } catch {
        if (attempts >= maxAttempts) {
          if (pollRef.current) {
            clearInterval(pollRef.current);
            pollRef.current = null;
          }
          setPaymentState({ status: 'failed', paid_at: null });
          setIsVerifyingPayment(false);
        }
      }
    };

    void poll();
    pollRef.current = setInterval(poll, 3000);

    return () => {
      if (pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
    };
  }, [accessToken, dispatch, needsPaymentVerification, paymentId, tenantSlug]);

  useEffect(() => {
    if (canceled) {
      return;
    }

    const canHydrate =
      Boolean(resolvedAppointmentId) &&
      (!needsPaymentVerification || paymentConfirmed);

    if (!canHydrate || hydrationStartedRef.current) {
      return;
    }

    hydrationStartedRef.current = true;

    const fallbackSummary = mergeAppointmentSummary(
      mapStoredContextToSummary(stableStoredContext),
      reduxAppointment as Partial<AppointmentSummary>
    );

    if (!fallbackSummary) {
      setHydrationState('loading');
    }

    if (!accessToken) {
      if (fallbackSummary) {
        applySummary(fallbackSummary);
      } else {
        setHydrationState('error');
      }

      return;
    }

    getAppointmentDetails(resolvedAppointmentId!, accessToken)
      .then((details) => {
        const hydrated = mapAppointmentDetailsToSummary(
          details,
          fallbackSummary ?? undefined
        );

        if (isSummaryComplete(hydrated)) {
          applySummary(hydrated as AppointmentSummary);
          return;
        }

        if (fallbackSummary) {
          applySummary(fallbackSummary);
          return;
        }

        setHydrationState('error');
      })
      .catch(() => {
        if (fallbackSummary) {
          applySummary(fallbackSummary);
          return;
        }

        setHydrationState('error');
      });
  }, [
    accessToken,
    applySummary,
    canceled,
    needsPaymentVerification,
    paymentConfirmed,
    reduxAppointment,
    resolvedAppointmentId,
    stableStoredContext,
  ]);

  useEffectOnce(() => {
    if (canceled) {
      return;
    }

    if (paymentId || stableStoredContext) {
      return;
    }

    const appointmentData = reduxAppointment;

    if (
      !appointmentData.date ||
      appointmentData.date === '' ||
      !appointmentData.time_from ||
      appointmentData.time_from === ''
    ) {
      if (!eventSlug) {
        return;
      }

      if (tenantSlug) {
        if (clientSlug) {
          appointmentId
            ? navigate(
                `/${tenantSlug}/${clientSlug}/${eventSlug}/${appointmentId}`
              )
            : navigate(`/${tenantSlug}/${clientSlug}/${eventSlug}`);
        } else {
          appointmentId
            ? navigate(`/${tenantSlug}/${eventSlug}/${appointmentId}`)
            : navigate(`/${tenantSlug}/${eventSlug}`);
        }
      } else if (clientSlug) {
        appointmentId
          ? navigate(`/${clientSlug}/${eventSlug}/${appointmentId}`)
          : navigate(`/${clientSlug}/${eventSlug}`);
      } else {
        appointmentId
          ? navigate(`/${eventSlug}/${appointmentId}`)
          : navigate(`/${eventSlug}`);
      }
    }
  });

  useEffect(() => {
    if (textColor) {
      const height =
        document.getElementsByClassName('mainBody')[0].scrollHeight + 40;
      window.parent.postMessage({ height }, '*');
    }
  });

  const stripHtml = (html: string) => {
    const tmp = document.createElement('DIV');
    tmp.innerHTML = html;
    return tmp.textContent || tmp.innerText || '';
  };

  const appointmentData = effectiveAppointment;
  const isRoundRobin = isRoundRobinEvent(appointmentData?.appointment);

  const eventTitle =
    isRoundRobin && !appointmentData?.host
      ? appointmentData?.title || ''
      : appointmentData?.host
      ? `${appointmentData?.title} ${formatMessage({ id: 'with' })} ${
          appointmentData?.host
        }`
      : appointmentData?.title || '';

  const eventConfig: CalendarEvent = {
    title: eventTitle,
    description: appointmentData?.description
      ? stripHtml(appointmentData.description)
      : '',
    start: new Date(`${appointmentData?.date}T${appointmentData?.time_from}`),
    end: new Date(`${appointmentData?.date}T${appointmentData?.time_to}`),
    location: appointmentData?.servedAt ?? '',
  };

  const handleGoogleCalendar = async () => {
    const appointmentNr = appointmentData?.appointment_nr || appointmentId;
    if (!appointmentNr || !eventSlug) {
      return;
    }

    if (tenantSlug) {
      dispatch(setTenantSlug(tenantSlug));
    }

    setIsLoadingHangout(true);

    try {
      await new Promise((resolve) => setTimeout(resolve, 5000));

      const response = await getHangoutLink(
        appointmentNr,
        eventSlug,
        clientSlug || undefined
      );
      const hangoutLink =
        response?.data.appointment?.hangout_link ||
        response?.data.appointment?.hangout_link;

      if (hangoutLink) {
        setHangoutLinkState(hangoutLink);
      }

      if (response?.data.appointment?.calendar_event_data) {
        setCalendarEventData({
          description:
            response.data.appointment.calendar_event_data.description,
          attendees: response.data.appointment.calendar_event_data.attendees,
        });
      }

      setIsLoadingHangout(false);

      const baseUrl = 'https://calendar.google.com/calendar/render';

      const description =
        response?.data.appointment?.calendar_event_data?.description || '';

      const params = new URLSearchParams({
        action: 'TEMPLATE',
        text: eventConfig.title || '',
        dates: `${formatDateForGoogleCalendar(
          eventConfig.start
        )}/${formatDateForGoogleCalendar(eventConfig.end)}`,
        details: description,
        location: hangoutLink || '',
      });

      if (
        response?.data.appointment?.calendar_event_data?.attendees &&
        !isRoundRobin
      ) {
        params.set(
          'add',
          response.data.appointment.calendar_event_data.attendees
        );
      }

      window.location.href = `${baseUrl}?${params.toString()}`;
    } catch {
      setIsLoadingHangout(false);
    }
  };

  const formatDateForGoogleCalendar = (date: Date) => {
    return date.toISOString().replace(/-|:|\.\d+/g, '');
  };

  const showPaymentVerification =
    Boolean(needsPaymentVerification) &&
    (isVerifyingPayment ||
      !paymentState?.status ||
      paymentState.status === 'pending' ||
      paymentFailed);

  const showSummaryLoading =
    !canceled &&
    !showPaymentVerification &&
    hydrationState === 'loading' &&
    !appointmentData;

  const showSummaryError =
    !canceled && !showPaymentVerification && hydrationState === 'error';

  if (showPaymentVerification) {
    return (
      <Container className="background">
        <InfoContainer className="background">
          <Flex
            direction={'column'}
            justify={'center'}
            align={'center'}
            gap={'16px'}
          >
            {paymentFailed ? (
              <>
                <h1 className="SemiBold" style={{ color: '#dc2626' }}>
                  {formatMessage({ id: 'payment.failedTitle' })}
                </h1>
                <p>{formatMessage({ id: 'payment.notCompleted' })}</p>
              </>
            ) : (
              <>
                <Loader size="md" color="black" />
                <h1 className="SemiBold">
                  {formatMessage({ id: 'payment.verifying' })}
                </h1>
              </>
            )}
          </Flex>
          <p className="success-footer link">
            {formatMessage({ id: 'poweredBy' })}
            <a
              href="https://jadwalh.com"
              target="_blank"
              rel="noreferrer"
              className="learnMore"
            >
              {' '}
              {formatMessage({ id: 'learnMore' })}
            </a>
          </p>
        </InfoContainer>
      </Container>
    );
  }

  if (showSummaryLoading) {
    return (
      <Container className="background">
        <InfoContainer className="background">
          <Flex direction="column" justify="center" align="center" gap="16px">
            <Loader size="md" color="black" />
            <h1 className="SemiBold">
              {formatMessage({ id: 'payment.verifying' })}
            </h1>
          </Flex>
        </InfoContainer>
      </Container>
    );
  }

  if (showSummaryError) {
    return (
      <Container className="background">
        <InfoContainer className="background">
          <Flex direction="column" justify="center" align="center" gap="16px">
            <h1 className="SemiBold" style={{ color: '#dc2626' }}>
              {formatMessage({ id: 'payment.summaryLoadFailed' })}
            </h1>
            <Button
              onClick={() => {
                hydrationStartedRef.current = false;
                setHydrationState('loading');
                window.location.reload();
              }}
            >
              {formatMessage({ id: 'payment.summaryRetry' })}
            </Button>
          </Flex>
        </InfoContainer>
      </Container>
    );
  }

  return (
    (appointmentData || canceled) && (
      <Container className="background">
        {canceled ||
        Number(appointmentData?.visible_to) === 0 ||
        Number(appointmentData?.visible_to) === 1 ? (
          <InfoContainer className="background">
            <Flex
              direction={'column'}
              justify={'center'}
              align={'center'}
              gap={'8px'}
            >
              {canceled ? (
                <h1 className="SemiBold">
                  {formatMessage({ id: 'cancellationSuccess' })}
                </h1>
              ) : (
                <>
                  <h1 className="SemiBold">
                    {appointmentData?.rescheduled
                      ? formatMessage({ id: 'rescheduleAppointmentSuccess' })
                      : formatMessage({ id: 'appointmentSuccess' })}
                  </h1>
                  <p>{formatMessage({ id: 'detailsSent' })}</p>
                </>
              )}
            </Flex>
            <Flex direction={'column'} gap="12px">
              {appointmentData?.time_from && (
                <EventInfo
                  isLastStep={true}
                  title={appointmentData?.title}
                  date={appointmentData?.date}
                  time={appointmentData?.time_from}
                  timeTo={appointmentData?.time_to}
                  timeZone={appointmentData?.timeZoneName}
                  servedAt={appointmentData?.servedAt}
                  served_at_value={appointmentData?.served_at_value}
                  served_at_data={appointmentData?.served_at_data}
                  appointment={appointmentData?.appointment}
                  hide_duration={appointmentData?.hide_duration}
                  price={appointmentData?.price}
                  has_e_payment={appointmentData?.has_e_payment}
                  currency_id={appointmentData?.currency}
                  currency_label={appointmentData?.currency_label}
                  host={appointmentData?.host}
                  description={appointmentData?.description}
                  duration={appointmentData?.duration}
                  oldAppointment={appointmentData?.oldAppointment}
                />
              )}
              {!canceled && appointmentData?.ics && (
                <ButtonContainer className="calenders">
                  <p style={{ textAlign: 'center' }}>
                    {formatMessage({ id: 'addEventToCalender' })}
                  </p>
                  <AddToCalendarLink
                    id="apple-calendar-btn"
                    href={appointmentData.ics}
                    target="_blank"
                  >
                    <p
                      style={{
                        color: '#000',
                        fontWeight: '600',
                        textDecoration: 'none !important',
                      }}
                    >
                      Apple
                    </p>
                  </AddToCalendarLink>
                  <AddToCalendarButton
                    id="google-calendar-btn"
                    onClick={handleGoogleCalendar}
                    disabled={isLoadingHangout}
                  >
                    {isLoadingHangout ? (
                      <Loader size="sm" color="black" />
                    ) : (
                      <GoogleIcon />
                    )}
                  </AddToCalendarButton>
                  <AddToCalendarLink
                    id="apple-calendar-btn"
                    href={appointmentData.ics}
                    target="_blank"
                  >
                    <ICS />
                  </AddToCalendarLink>
                </ButtonContainer>
              )}
            </Flex>

            <p className="success-footer link">
              {!canceled && (
                <p
                  style={{
                    textAlign: 'center',
                    paddingBottom: '24px',
                    marginBottom: '24px',
                    borderBottom: `1px solid rgb(225, 225, 225)`,
                  }}
                >
                  {formatMessage({ id: 'successFooterInfo' })}
                </p>
              )}
              {formatMessage({ id: 'poweredBy' })}
              <a
                href="https://jadwalh.com"
                target="_blank"
                rel="noreferrer"
                className="learnMore"
              >
                {' '}
                {formatMessage({ id: 'learnMore' })}
              </a>
            </p>
          </InfoContainer>
        ) : (
          <InfoSpecialContainer>
            <Flex
              direction={'column'}
              justify={'center'}
              align={'center'}
              w={'100%'}
              gap="32px"
            >
              <Flex
                direction={'column'}
                align={'center'}
                justify={'center'}
                gap="32px"
              >
                <SuccessRes />
                <Flex
                  direction={'column'}
                  align={'center'}
                  justify={'center'}
                  gap="8px"
                >
                  <p
                    className="reservation-title"
                    style={{
                      marginBottom: `${
                        appointmentData?.rescheduled ? '0.5rem' : '0rem'
                      }`,
                    }}
                  >
                    {formatMessage({ id: 'meetingCancelled' })}
                  </p>
                  <h3
                    style={{
                      marginBottom: `${
                        appointmentData?.rescheduled ? '0rem' : '0rem'
                      }`,
                    }}
                  >
                    {appointmentData?.host}
                  </h3>
                </Flex>
              </Flex>
              <Flex gap={2}>
                <p>
                  {dayjs(appointmentData?.date)
                    .locale(langType)
                    .format('dddd DD MMMM YYYY')}
                  ,
                </p>
                <p>
                  {appointmentData?.time_from &&
                    appointmentData?.time_from !== '' &&
                    convertTimeFormatTo12(appointmentData?.time_from, langType)}
                </p>
              </Flex>
              {appointmentData?.rescheduled ? (
                <p style={{ textAlign: 'center', marginTop: '-24px' }}>
                  {formatMessage({ id: 'infoWasSentToYourEmail' })}
                </p>
              ) : (
                <ButtonDiv>
                  {formatMessage({ id: 'infoWasSentToYourEmail' })}
                </ButtonDiv>
              )}
              {appointmentData?.ics && (
                <ButtonContainer>
                  <AddToCalendarButton
                    id="google-calendar-btn"
                    onClick={handleGoogleCalendar}
                    disabled={isLoadingHangout}
                  >
                    {isLoadingHangout ? (
                      <Loader size="sm" color="white" />
                    ) : (
                      formatMessage({ id: 'addEventToGoogleCalendar' })
                    )}
                  </AddToCalendarButton>
                  <AddToCalendarLink
                    id="apple-calendar-btn"
                    href={appointmentData.ics}
                    target="_blank"
                  >
                    {formatMessage({ id: 'addEventToAppleCalendar' })}
                  </AddToCalendarLink>
                </ButtonContainer>
              )}
            </Flex>
            <p className="success-footer link">
              {formatMessage({ id: 'poweredBy' })}
              <a
                href="https://app.jadwalh.com"
                target="_blank"
                rel="noreferrer"
              >
                {' '}
                {formatMessage({ id: 'Jadwalh' })}
              </a>
            </p>
          </InfoSpecialContainer>
        )}
      </Container>
    )
  );
};

export default SuccessReservation;
